import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { CopyApiKeyButton } from '@/components/atoms/CopyApiKeyButton/CopyApiKeyButton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { popModal } from '@/redux/modals/actions';

type ModalIntegrationApiKeyCreatedProps = { apiKey: string; integrationName: string };

/**
 * Modal displayed after generating an API key for a Custom HTTP integration.
 * Shows the API key once with a copy button and a warning that it won't be shown again.
 */
export const ModalIntegrationApiKeyCreated = ({
  apiKey,
  integrationName,
}: ModalIntegrationApiKeyCreatedProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setOpen(false);
        dispatch(popModal());
        return;
      }
      setOpen(true);
    },
    [dispatch]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="space-y-3 p-6 sm:max-w-xl sm:p-8"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="space-y-1 text-left">
          <DialogTitle className="text-xl leading-6 font-semibold text-foreground">
            {t('integrations.custom.api_key_modal.title')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t('integrations.custom.api_key_modal.subtitle', { 0: integrationName })}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
          <p className="text-sm font-medium text-destructive">
            {t('integrations.custom.api_key_modal.warning')}
          </p>
        </div>

        <Textarea
          value={apiKey}
          rows={2}
          draggable={false}
          readOnly
          className="mt-3 w-full resize-none rounded-md border border-input bg-background p-3 font-mono text-xs leading-4 text-muted-foreground"
        />

        <CopyApiKeyButton apiKey={apiKey} />
      </DialogContent>
    </Dialog>
  );
};

export default ModalIntegrationApiKeyCreated;
